import dayjs from "dayjs";
import type { IntlShape } from "react-intl";
import { date, literal, object, string, enum as zodEnum } from "zod";

import { isValidPhoneNumber, parseE164 } from "@/components/Inputs/TelInput";
import type { TravellerAgeGroup } from "@/features/travellers/types/types";
import { MIN_ADULT_AGE, MIN_CHILD_AGE } from "@/utils/constants";

const nameMinLength = 2;
const nameMaxLength = 30;

export type TravellerAgeGroupConstraint = {
  ageGroup: TravellerAgeGroup;
  travelDate: Date;
  /** When set, the traveller's age at travelDate must match one of these values (used in hotel flow). */
  requiredAges?: number[];
};

export const getTravellerValidationSchema = (
  intl: IntlShape,
  ageGroupConstraint?: TravellerAgeGroupConstraint,
) => {
  const buildBirthDateSchema = () => {
    const base = date({
      required_error: intl.formatMessage({
        defaultMessage: "Date of birth is required",
        description: "Validation error when date of birth is not provided",
      }),
      invalid_type_error: intl.formatMessage({
        defaultMessage: "Date of birth is required",
        description: "Validation error when date of birth is not provided",
      }),
    })
      .max(
        new Date(),
        intl.formatMessage({
          defaultMessage: "Date of birth must be a past date",
          description:
            "Validation error when date of birth is set in the future",
        }),
      )
      .nullable()
      .refine((v) => v !== null, {
        message: intl.formatMessage({
          defaultMessage: "Date of birth is required",
          description: "Validation error when date of birth is not provided",
        }),
      });

    if (!ageGroupConstraint) return base;

    const { ageGroup, travelDate, requiredAges } = ageGroupConstraint;

    if (requiredAges !== undefined && requiredAges.length > 0) {
      return base.refine(
        (v) => {
          if (v === null) return true;
          const ageAtTravel = dayjs(travelDate).diff(dayjs(v), "year");
          return requiredAges.includes(ageAtTravel);
        },
        intl.formatMessage({
          defaultMessage: "Incorrect age for selected traveller type",
          description:
            "Validation error when the traveller's age does not match the required age.",
        }),
      );
    }

    if (ageGroup === "ADT") {
      const maxBirthDate = dayjs(travelDate)
        .subtract(MIN_ADULT_AGE, "year")
        .toDate();

      return base.refine(
        (v) => v === null || !dayjs(v).isAfter(maxBirthDate),
        intl.formatMessage({
          defaultMessage: "Incorrect age for selected traveller type",
          description:
            "Validation error when the traveller's age does not match the required age.",
        }),
      );
    }

    if (ageGroup === "CHD") {
      const maxBirthDate = dayjs(travelDate)
        .subtract(MIN_CHILD_AGE, "year")
        .toDate();
      const minBirthDate = dayjs(travelDate)
        .subtract(MIN_ADULT_AGE, "year")
        .add(1, "day")
        .toDate();

      return base
        .refine(
          (v) => v === null || !dayjs(v).isAfter(maxBirthDate),
          intl.formatMessage({
            defaultMessage: "Incorrect age for selected traveller type",
            description:
              "Validation error when the traveller's age does not match the required age.",
          }),
        )
        .refine(
          (v) => v === null || !dayjs(v).isBefore(minBirthDate),
          intl.formatMessage({
            defaultMessage: "Incorrect age for selected traveller type",
            description:
              "Validation error when the traveller's age does not match the required age.",
          }),
        );
    }

    // INF
    const minBirthDate = dayjs(travelDate)
      .subtract(MIN_CHILD_AGE, "year")
      .add(1, "day")
      .toDate();

    return base.refine(
      (v) => v === null || !dayjs(v).isBefore(minBirthDate),
      intl.formatMessage({
        defaultMessage: "Incorrect age for selected traveller type",
        description:
          "Validation error when the traveller's age does not match the required age.",
      }),
    );
  };

  const birthDate = buildBirthDateSchema();

  return object({
    firstName: string()
      .min(
        1,
        intl.formatMessage({
          defaultMessage: "Name is required",
          description: "Validation error when first name is not provided",
        }),
      )
      .regex(
        /^[a-zA-Z \-']*$/,
        intl.formatMessage({
          defaultMessage: "Name is not valid",
          description:
            "Validation error when the first name contains invalid characters",
        }),
      )
      .min(
        nameMinLength,
        intl.formatMessage(
          {
            defaultMessage: "Name should be between {min} and {max} characters",
            description:
              "Validation error when the first name does not meet length requirements",
          },
          {
            min: nameMinLength,
            max: nameMaxLength,
          },
        ),
      )
      .max(
        nameMaxLength,
        intl.formatMessage(
          {
            defaultMessage: "Name should be between {min} and {max} characters",
            description:
              "Validation error when the first name does not meet length requirements",
          },
          {
            min: nameMinLength,
            max: nameMaxLength,
          },
        ),
      ),
    lastName: string()
      .min(
        1,
        intl.formatMessage({
          defaultMessage: "Surname is required",
          description: "Validation error when surname is not provided",
        }),
      )
      .regex(
        /^[a-zA-Z \-']*$/,
        intl.formatMessage({
          defaultMessage: "Surname is not valid",
          description:
            "Validation error when surname contains invalid characters",
        }),
      )
      .min(
        nameMinLength,
        intl.formatMessage(
          {
            defaultMessage:
              "Surname should be between {min} and {max} characters",
            description:
              "Validation error when surname does not meet length requirements",
          },
          {
            min: nameMinLength,
            max: nameMaxLength,
          },
        ),
      )
      .max(
        nameMaxLength,
        intl.formatMessage(
          {
            defaultMessage:
              "Surname should be between {min} and {max} characters",
            description:
              "Validation error when surname does not meet length requirements",
          },
          {
            min: nameMinLength,
            max: nameMaxLength,
          },
        ),
      ),
    birthDate,
    passportNumber: string()
      .min(
        1,
        intl.formatMessage({
          defaultMessage: "Passport ID is required",
          description:
            "Validation error when the passport number is not provided",
        }),
      )
      .regex(
        /^[a-zA-Z0-9]{7,9}$/,
        intl.formatMessage({
          defaultMessage: "Passport ID is invalid",
          description:
            "Validation error when the passport number does not match the required format",
        }),
      ),
    emailAddress: string()
      .min(
        1,
        intl.formatMessage({
          defaultMessage: "Email is required",
          description: "Validation error when email address is not provided",
        }),
      )
      .email(
        intl.formatMessage({
          defaultMessage: "Email is not valid",
          description:
            "Validation error when the email address format is invalid",
        }),
      ),
    passportIssuingDate: date({
      required_error: intl.formatMessage({
        defaultMessage: "Issue date is required",
        description:
          "Validation error when passport issue date is not provided",
      }),
      invalid_type_error: intl.formatMessage({
        defaultMessage: "Issue date is required",
        description:
          "Validation error when passport issue date is not provided",
      }),
    })
      .max(
        new Date(),
        intl.formatMessage({
          defaultMessage: "Issue date must be a past date",
          description:
            "Validation error when passport issue date is set in the future",
        }),
      )
      .nullable()
      .refine((v) => v !== null, {
        message: intl.formatMessage({
          defaultMessage: "Issue date is required",
          description:
            "Validation error when passport issue date is not provided",
        }),
      }),
    passportExpiryDate: date({
      required_error: intl.formatMessage({
        defaultMessage: "Expiry date is required",
        description:
          "Validation error when passport expiry date is not provided",
      }),
      invalid_type_error: intl.formatMessage({
        defaultMessage: "Expiry date is required",
        description:
          "Validation error when passport expiry date is not provided",
      }),
    })
      .min(
        dayjs().subtract(1, "day").toDate(),
        intl.formatMessage({
          defaultMessage: "Expiry date must be a future date",
          description:
            "Validation error when passport expiry date is in the past (passport expired)",
        }),
      )
      .nullable()
      .refine((v) => v !== null, {
        message: intl.formatMessage({
          defaultMessage: "Expiry date is required",
          description:
            "Validation error when passport expiry date is not provided",
        }),
      }),
    title: zodEnum(["mr", "mrs", "miss", "ms", "mstr"], {
      required_error: intl.formatMessage({
        defaultMessage: "Title is required",
        description:
          "Validation error when traveller title (Mr / Mrs / Miss / Master) is not selected",
      }),
    }),
    phonePrefix: string().min(1),
    phoneNumber: string()
      .min(
        1,
        intl.formatMessage({
          defaultMessage: "Phone number is not valid",
          description:
            "Validation error when the phone number format is invalid",
        }),
      )
      .refine(
        (value) => {
          const phoneValue = value ?? "";
          const { country } = parseE164(phoneValue);

          return country ? isValidPhoneNumber(phoneValue, country) : false;
        },
        intl.formatMessage({
          defaultMessage: "Phone number is not valid",
          description:
            "Validation error when the phone number format is invalid",
        }),
      ),
    passportNationality: literal("IQ"),
    passportIssuingCountry: literal("IQ"),
  });
};
